import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaGift,
  FaPlayCircle,
  FaLock,
  FaEnvelope,
  FaUser,
} from "react-icons/fa";
import backgroundImage from "./illustration_login.webp";
import useScrollToTop from "./../../hooks/useScollToTop";
import { registerUser, checkEmailExists } from "../../services/userServices";
import Modal from "../../hooks/Modal";
import Button from "../../components/Button/Button";

function RegisterPage() {
  useScrollToTop();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [selectedRole, setSelectedRole] = useState(7);
  const [errorMessage, setErrorMessage] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleResetPassword = () => {
    closeModal();
    navigate("/forgot-password");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    try {
      setIsSubmitting(true);

      const emailCheck = await checkEmailExists(email);

      if (emailCheck.error) {
        setErrorMessage(emailCheck.error);
        return;
      }

      if (emailCheck.exists) {
        openModal();
        return;
      }

      const result = await registerUser(
        nom,
        prenom,
        email,
        password,
        selectedRole,
      );

      if (result.error) {
        setErrorMessage(result.error);
        return;
      }

      navigate("/login", {
        state: {
          successMessage:
            selectedRole === 7
              ? "Votre compte Presence Free a bien été créé. Vous pouvez maintenant vous connecter."
              : "Votre compte Démo a bien été créé. Vous pouvez maintenant vous connecter.",
        },
      });
    } catch (error) {
      setErrorMessage("Une erreur est survenue lors de l'inscription.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleOptions = [
    {
      id: 7,
      title: "Presence Free",
      badge: "Recommandé",
      icon: <FaGift className="text-base" />,
      description:
        "Utilisez réellement l'application gratuitement pour démarrer.",
      features: ["Jusqu'à 50 adhérents", "2 groupes maximum", "1 lieu maximum"],
    },
    {
      id: 2,
      title: "Mode Démo",
      badge: "Découverte",
      icon: <FaPlayCircle className="text-base" />,
      description:
        "Découvrez l'interface et testez le fonctionnement de l'application.",
      features: [
        "Découverte rapide",
        "Test de l'interface",
        "Données fictives",
      ],
    },
  ];

  return (
    <div
      className="relative min-h-screen overflow-x-hidden bg-slate-950"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[2px]" />

      <div className="relative z-10 flex min-h-screen items-start justify-center px-3 py-3 sm:px-4 sm:py-5 md:items-center md:py-8">
        <div className="w-full max-w-[980px] overflow-hidden rounded-2xl border border-white/10 bg-white/95 shadow-2xl dark:border-slate-700/60 dark:bg-slate-900/95 md:rounded-3xl">
          <div className="grid md:grid-cols-[0.88fr_1.12fr]">
            {/* Colonne branding desktop */}
            <div className="hidden md:flex flex-col justify-between bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-6 text-white lg:p-8">
              <div>
                <p className="mb-3 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                  Club Présence Pro
                </p>

                <h1 className="text-2xl font-black leading-tight lg:text-3xl">
                  Créez votre compte et choisissez votre formule de départ
                </h1>

                <p className="mt-5 max-w-md text-sm leading-6 text-white/80">
                  Commencez soit avec une vraie formule gratuite limitée, soit
                  avec un mode démo pour découvrir la plateforme.
                </p>
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <FaCheckCircle className="mt-1 shrink-0 text-green-400" />
                  <p className="text-sm text-white/85">
                    Gérez vos présences, groupes et lieux simplement.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <FaCheckCircle className="mt-1 shrink-0 text-green-400" />
                  <p className="text-sm text-white/85">
                    Démarrez gratuitement puis passez à une formule payante si
                    votre club grandit.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <FaCheckCircle className="mt-1 shrink-0 text-green-400" />
                  <p className="text-sm text-white/85">
                    Une inscription simple, rapide et plus claire pour
                    l’utilisateur.
                  </p>
                </div>
              </div>
            </div>

            {/* Colonne contenu */}
            <div className="p-4 sm:p-6 md:p-7 lg:p-8">
              <div className="mx-auto w-full max-w-none">
                {/* Intro mobile */}
                <div className="mb-5 md:hidden">
                  <div className="rounded-2xl border border-white/10 bg-slate-900/80 p-4 text-white shadow-lg backdrop-blur-sm">
                    <p className="mb-2 inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
                      Club Présence Pro
                    </p>

                    <h1 className="text-xl font-black leading-tight">
                      Créez votre compte et choisissez votre formule de départ
                    </h1>

                    <p className="mt-2 text-sm leading-6 text-white/80">
                      Commencez soit avec une vraie formule gratuite limitée,
                      soit avec un mode démo pour découvrir la plateforme.
                    </p>

                    <div className="mt-4 space-y-2">
                      <div className="flex items-start gap-2">
                        <FaCheckCircle className="mt-0.5 shrink-0 text-green-400" />
                        <p className="text-sm text-white/85">
                          Gérez vos présences, groupes et lieux simplement.
                        </p>
                      </div>

                      <div className="flex items-start gap-2">
                        <FaCheckCircle className="mt-0.5 shrink-0 text-green-400" />
                        <p className="text-sm text-white/85">
                          Démarrez gratuitement puis évoluez selon la taille du
                          club.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-5 text-center md:text-left">
                  <h2 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100 sm:text-3xl">
                    Inscription
                  </h2>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                    Choisissez votre formule puis créez votre compte.
                  </p>
                </div>

                {errorMessage && (
                  <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                    {errorMessage}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid gap-5 lg:grid-cols-[240px_minmax(0,1fr)] lg:items-start">
                    {/* Choix formule */}
                    <div className="space-y-3">
                      <label className="block text-sm font-semibold text-slate-800 dark:text-slate-200">
                        Choisissez votre formule
                      </label>

                      <div className="space-y-3">
                        {roleOptions.map((role) => {
                          const isSelected = selectedRole === role.id;

                          return (
                            <button
                              key={role.id}
                              type="button"
                              onClick={() => setSelectedRole(role.id)}
                              aria-pressed={isSelected}
                              className={`w-full rounded-2xl border p-3 text-left transition-all duration-200 ${
                                isSelected
                                  ? "border-blue-600 bg-blue-50 shadow-md ring-2 ring-blue-100 dark:border-blue-400 dark:bg-blue-500/10 dark:ring-blue-500/20"
                                  : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm dark:border-slate-700 dark:bg-slate-800/60 dark:hover:border-slate-600"
                              }`}
                            >
                              <div className="mb-2 flex items-center justify-between gap-2">
                                <div
                                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ${
                                    isSelected
                                      ? "bg-blue-600 text-white dark:bg-blue-500"
                                      : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                                  }`}
                                >
                                  {role.icon}
                                  {role.title}
                                </div>

                                {role.badge ? (
                                  <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
                                    {role.badge}
                                  </span>
                                ) : null}
                              </div>

                              <p className="mb-2 text-sm text-slate-600 dark:text-slate-300">
                                {role.description}
                              </p>

                              <ul className="space-y-1.5">
                                {role.features.map((feature) => (
                                  <li
                                    key={feature}
                                    className="flex items-center gap-2 text-[13px] text-slate-700 dark:text-slate-200"
                                  >
                                    <FaCheckCircle className="shrink-0 text-green-500" />
                                    <span>{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Formulaire */}
                    <div className="space-y-4">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                            Nom
                          </label>
                          <div className="relative">
                            <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                              type="text"
                              placeholder="Votre nom"
                              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                              value={nom}
                              onChange={(e) => setNom(e.target.value)}
                              autoComplete="family-name"
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                            Prénom
                          </label>
                          <div className="relative">
                            <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                            <input
                              type="text"
                              placeholder="Votre prénom"
                              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                              value={prenom}
                              onChange={(e) => setPrenom(e.target.value)}
                              autoComplete="given-name"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                          Email
                        </label>
                        <div className="relative">
                          <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                          <input
                            type="email"
                            placeholder="Votre email"
                            className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                          Mot de passe
                        </label>
                        <div className="relative">
                          <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                          <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Choisissez un mot de passe"
                            className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-12 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-400 dark:focus:ring-blue-500/20"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            minLength={6}
                            autoComplete="new-password"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                            aria-label={
                              showPassword
                                ? "Masquer le mot de passe"
                                : "Afficher le mot de passe"
                            }
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                          Minimum 6 caractères.
                        </p>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-200">
                        {selectedRole === 7 ? (
                          <span>
                            <strong>Presence Free</strong> : idéal pour démarrer
                            gratuitement avec un petit club, limité à 50
                            adhérents, 2 groupes et 1 lieu.
                          </span>
                        ) : (
                          <span>
                            <strong>Mode Démo</strong> : parfait pour découvrir
                            rapidement l’application avant une utilisation
                            réelle.
                          </span>
                        )}
                      </div>

                      <button
                        className="inline-flex w-full items-center justify-center rounded-2xl bg-blue-600 px-4 py-3 font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-blue-500 dark:hover:bg-blue-600"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Création du compte..." : "S'inscrire"}
                      </button>

                      <p className="text-center text-sm text-slate-600 dark:text-slate-400">
                        Déjà inscrit ?{" "}
                        <button
                          type="button"
                          onClick={() => navigate("/login")}
                          className="font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          Se connecter
                        </button>
                      </p>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={closeModal}>
          <p className="mb-4 text-center dark:text-slate-100">
            Le mail est déjà enregistré. Souhaitez-vous réinitialiser votre mot
            de passe ?
          </p>
          <div className="flex justify-center gap-4">
            <Button text="Oui" onClick={handleResetPassword} />
            <Button text="Non" onClick={closeModal} />
          </div>
        </Modal>
      )}
    </div>
  );
}

export default RegisterPage;
